import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { appTheme } from '../../styles/theme';

const Accordion = styled.aside`
  display: flex;
  flex-direction: column;
  background: ${appTheme.surface};
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusLg};
  overflow: hidden;
  box-shadow: ${appTheme.shadowSm};
`;

const Section = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid ${appTheme.border};
  }
`;

const SectionHeader = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: ${({ $open }) => ($open ? appTheme.mint : appTheme.surface)};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: ${appTheme.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: -2px;
  }
`;

const SectionTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};
`;

const Chevron = styled.span<{ $open: boolean }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-right: 2px solid ${appTheme.textMuted};
  border-bottom: 2px solid ${appTheme.textMuted};
  transform: rotate(${({ $open }) => ($open ? '225deg' : '45deg')});
  transition: transform 0.2s ease;
  margin-top: ${({ $open }) => ($open ? '2px' : '-2px')};
`;

const SectionBody = styled.div`
  padding: 0 16px 16px;
`;

interface SidebarAccordionSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const SidebarAccordionSection: React.FC<SidebarAccordionSectionProps> = ({
  title,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <Section>
      <SectionHeader
        type="button"
        $open={open}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((current) => !current)}
      >
        <SectionTitle>{title}</SectionTitle>
        <Chevron $open={open} aria-hidden />
      </SectionHeader>
      {open && <SectionBody id={bodyId}>{children}</SectionBody>}
    </Section>
  );
};

interface SidebarAccordionProps {
  children: React.ReactNode;
}

const SidebarAccordion: React.FC<SidebarAccordionProps> = ({ children }) => (
  <Accordion>{children}</Accordion>
);

export default SidebarAccordion;
